import { Link } from 'react-router';

import { isStdTestCourse } from 'my-core/course-utils';
import { formatDate } from 'my-core/date-utils';
import { onlineCourseGatedContentCTAClicked } from 'my-core/gtm-events';
import { useCurrentUser, useStandardApiRequest } from 'my-core/hooks';
import { getIsPurchased } from 'my-core/online-course-user-utils';
import { getPackagePricing, isSubscriptionOffering } from 'my-core/online-course-utils';
import { stdTestCourseLandingPath } from 'my-core/routes';
import {
  FREE_TOKEN_COUNT,
  getHasPaymentIssue,
  getPauseEndDate,
  getSubscriptionStatus,
} from 'my-core/subscription-utils';
import { TOKEN_CREDIT_TASK_CONFIG } from 'my-core/token-utils';
// import { tokensDashboardPath } from 'my-core/routes';
import { capitalize, pluralize } from 'my-utils';

// import LoginProtected from 'my-components/LoginProtected';
import WizeToken from 'my-components/WizeToken';

// import { Link } from 'react-router';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// import unpublished from 'images/artwork/icons/engineering.svg';
import { updateUserCourseLsacInfo } from 'my-actions/UserCourseActions';

import timer from 'images/artwork/icons/timer.svg';
import activityLocked from 'images/icons/lock-primary.svg';

const rootCss = ({ breakpoints, palette, shadows, spacing }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 350,

  '.GC-avatar': {
    width: 40,
    height: 40,
    marginBottom: spacing(1.5),
    backgroundColor: palette.background.paper,
    boxShadow: shadows.states.static,
    '& > img': { height: 25, width: 'auto' },
  },
  '.GC-subtitle': { marginBottom: spacing(1.5) },
  '.GC-actionBtns': {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: spacing(0, -0.5),
    '& > *': {
      margin: spacing(0.5),
      minWidth: 150,
    },
  },
  '.GC-secondaryBtn': { backgroundColor: 'rgba(255,255,255,0.7)' },
  '.GC-token': { minWidth: 0, margin: spacing(0.5, 2) },

  [breakpoints.up('xl')]: {
    '.GC-avatar': { marginBottom: spacing(3) },
    '.GC-subtitle': { marginBottom: spacing(3) },
  },
  [breakpoints.only('xs')]: {
    '.GC-avatar': {
      width: 40,
      height: 40,
      marginBottom: spacing(0.5),
    },
    '.GC-subtitle': { marginBottom: spacing(1) },
  },
});

export default function GatedContent(props) {
  const {
    actionText = 'start studying',
    activityText = 'Activity',
    className,
    courseSubscription,
    eventTrackingProperties,
    hideIcon,
    onActionClicked,
    onlineCourse,
    // onStartTrial,
    onlineCourseUser,
    onPurchase,
    onTokenPurchase,
    packageMode = 'pro',
    previewEnded,
    subscription,
    // subscriptionTrialStatus,
    tokenPrice = 1,
    tokenPurchaseStatus,
    userCourse,
  } = props;

  const currentUser = useCurrentUser();

  const blockedText = previewEnded ? 'Preview Ended' : 'Locked';
  const isSubOffering = isSubscriptionOffering(onlineCourse);

  // LSAT Course Action
  const { performRequest: performLsacCheck, requestStatus: lsacCheckStatus } = useStandardApiRequest({
    actionCreator: updateUserCourseLsacInfo,
    errorMessage: true,
  });

  const blockedReason = activityBlockedReason({
    currentUser,
    onlineCourse,
    onlineCourseUser,
    userCourse,
    subscription,
    courseSubscription,
    packageMode,
  });

  switch (blockedReason) {
    case 'account_required':
      return renderParts(
        activityLocked,
        `${activityText} ${blockedText}`,
        `Create a free account to ${actionText}`,
        renderPurchaseButton('Sign Up'),
      );
    // case 'trial_not_started':
    //   return renderParts(
    //     activityLocked,
    //     `${activityText} ${blockedText}`,
    //     `Start a ${TRIAL_DURATION_DAYS} day free trial to ${actionText}`,
    //     <Button disabled={!!subscriptionTrialStatus?.fetching} onClick={onStartTrial} variant="contained">
    //       Start Free Trial
    //     </Button>
    //   );
    case 'payment_processing_issue':
      return renderParts(
        activityLocked,
        'Payment Problem',
        `Update your payment information to ${actionText}`,
        renderPurchaseButton('Update Card'),
      );
    case 'access_paused':
      return renderParts(
        activityLocked,
        'Subscription Paused',
        `paused until ${formatDate(getPauseEndDate(subscription), 'upcomingDate')}`,
      );
    case 'trial_ended':
      return renderParts(
        activityLocked,
        'Trial Ended',
        `Upgrade now to ${actionText}`,
        renderPurchaseButton('Upgrade Now'),
      );
    case 'purchase_required':
      return renderParts(
        activityLocked,
        `${activityText} ${blockedText}`,
        // `Upgrade now to ${actionText}`,
        // renderPurchaseButton('Upgrade Now')
        ...(isSubOffering && currentUser.token_balance >= tokenPrice ?
          [
            `Use your credits to ${actionText}`,
            // eslint-disable-next-line @eslint-react/no-missing-key
            <>
              <WizeToken className="GC-token" size={40}>
                {tokenPrice}
              </WizeToken>
              <Button
                color="primary"
                disabled={!!tokenPurchaseStatus?.fetching}
                onClick={onTokenPurchase}
                variant="contained"
              >
                {`Use ${pluralize(tokenPrice, 'Credit')}`}
              </Button>
            </>,
          ]
        : (
          TOKEN_CREDIT_TASK_CONFIG.account_confirmed &&
          !TOKEN_CREDIT_TASK_CONFIG.account_confirmed.disabled &&
          isSubOffering &&
          !currentUser.confirmed
        ) ?
          [
            `Verify your email to get your ${TOKEN_CREDIT_TASK_CONFIG.account_confirmed.value} free activities. Make sure to check your spam folder if you cannot find the email.`,
            renderPurchaseButton('Subscribe'),
          ]
        : onlineCourse.purchase_type === 'course_bundle' && isStdTestCourse(onlineCourse.course_slug) ?
          [
            `${capitalize(actionText)} and more by enrolling in our ${onlineCourse.course_code} program`,
            // eslint-disable-next-line @eslint-react/no-missing-key
            <Button
              component={Link}
              nativeButton={false}
              to={stdTestCourseLandingPath(onlineCourse.course_slug)}
              variant="contained"
            >
              Learn More
            </Button>,
          ]
        : [
            `${isSubOffering ? 'Subscribe' : 'Purchase'} now to ${actionText}`,
            renderPurchaseButton(isSubOffering ? 'Subscribe' : 'Purchase'),
            /*<LoginProtected source="in_course_earn_tokens">
                <Button
                  className={classes.secondaryBtn}
                  color="primary"
                  component={Link}
                  to={tokensDashboardPath()}
                  variant="outlined"
                >
                  Earn More Credits
                </Button>
              </LoginProtected>*/
          ]),
      );
    case 'lsac_link_required':
      return renderParts(
        activityLocked,
        'LSAC Account Required',
        'Check your inbox for an email from LSAC.org. Once you create an account your access will be unlocked',
        <Button
          className="GC-secondaryBtn"
          disabled={!!lsacCheckStatus?.fetching}
          onClick={() => performLsacCheck(userCourse.id)}
          size="small"
          variant="outlined"
        >
          Re-Check LSAC Account
        </Button>,
      );
    case 'access_expired':
      return renderParts(timer, 'Access Expired', 'Your access to this course has expired');
    default:
      return null;
  }

  function renderParts(icon, primaryText, secondaryText, buttons) {
    return (
      <div className={className} css={rootCss}>
        {!hideIcon && <Avatar className="GC-avatar" src={icon} />}
        <Typography align="center" className="GC-title" variant="h6">
          {primaryText}
        </Typography>
        <Typography align="center" className="GC-subtitle" variant="body1">
          {secondaryText}
        </Typography>
        <div className="GC-actionBtns">{buttons}</div>
      </div>
    );
  }

  function renderPurchaseButton(text) {
    return (
      <Button
        onClick={() => {
          onActionClicked?.();
          onlineCourseGatedContentCTAClicked(text, eventTrackingProperties);
          onPurchase();
        }}
        variant="contained"
      >
        {text}
      </Button>
    );
  }

  function activityBlockedReason({
    currentUser,
    onlineCourse,
    onlineCourseUser,
    packageMode,
    subscription,
    // courseSubscription,
    userCourse,
  }) {
    const [price] = getPackagePricing(onlineCourse, packageMode);
    if (
      currentUser.guest &&
      !(onlineCourse.purchase_type === 'standard' && price) &&
      onlineCourse.purchase_type !== 'course_bundle' &&
      !(isSubOffering && !FREE_TOKEN_COUNT)
    ) {
      return 'account_required';
    }
    if (isSubOffering) {
      if (subscription) {
        if (getHasPaymentIssue(subscription)) {
          return 'payment_processing_issue';
        }
        if (getSubscriptionStatus(subscription) === 'paused') {
          return 'access_paused';
        }
        return 'trial_ended';
      } // else {
      //   return 'trial_not_started';
      // }
    }
    if (onlineCourse.purchase_type === 'course_bundle') {
      if (userCourse?.status === 'pending_payment' && userCourse.lsac_info) {
        return 'lsac_link_required';
      }
      return 'purchase_required';
    }
    if (!getIsPurchased(onlineCourseUser, packageMode)) {
      return 'purchase_required';
    }
    if (onlineCourseUser?.status === 'expired') {
      return 'access_expired';
    }
  }
}
